/**
 * API Configuration
 *
 * URLs are injected at runtime via window.__APP_CONFIG__ (see public/config.js).
 * The container entrypoint generates config.js from environment variables.
 *
 * The global is declared once in attain-ui-lib (runtime-env) as
 * ``AppConfigShape & Record<string, unknown>`` so each SPA can add its
 * own keys without re-augmenting Window. We read the ss-ui-specific
 * keys through a narrow cast below — keeping property accesses typed
 * while avoiding the TS2717 "subsequent property declarations must
 * have the same type" collision with the lib's declaration.
 */

import { appConfig } from './appConfig';

interface SsUiAppConfig {
  API_BASE_URL?: string;
  IDENTITY_API_BASE_URL?: string;
  ASSISTANT_API_BASE_URL?: string;
  ENTERPRISE_API_BASE_URL?: string;
  MIXPANEL_TOKEN?: string;
  FEATURES?: Record<string, boolean>;
}

const ssUiConfig: SsUiAppConfig = (window.__APP_CONFIG__ ?? {}) as SsUiAppConfig;

export const MIXPANEL_TOKEN = ssUiConfig.MIXPANEL_TOKEN ?? '';

export const API_CONFIG = {
  baseUrl: ssUiConfig.API_BASE_URL ?? '/api-ss',
  identityBaseUrl: ssUiConfig.IDENTITY_API_BASE_URL ?? '/api-identity',
  assistantBaseUrl: ssUiConfig.ASSISTANT_API_BASE_URL ?? '/api-agent',
  // The Enterprise API (ent-api, RFC-0501, port 8501). Only the ENTERPRISE
  // flavour ever calls it: ent-api's audience gate accepts `aud=enterprise`
  // alone, so a self-service token is refused 401 there by design. The value
  // is present in every build regardless, because it is a URL, not a feature —
  // what gates the surfaces is `appConfig.accountType`, never this string.
  enterpriseBaseUrl: ssUiConfig.ENTERPRISE_API_BASE_URL ?? '/api-ent',
  // Product flavour (RFC-0503): signals the account type to the Identity API
  // at provisioning. Config-driven via appConfig, which resolves the flavour
  // at build time (VITE_FLAVOUR) with a runtime override
  // (window.__APP_CONFIG__.ACCOUNT_TYPE). Defaults to 'selfserve' for this
  // build, matching the current server default.
  accountType: appConfig.accountType,
  timeout: 30000,

  endpoints: {
    // Auth (Identity API v1)
    login: '/v1/auth/login',
    callback: '/v1/auth/callback',
    tokenFromAzure: '/v1/auth/token-from-azure',
    me: '/v1/auth/me',
    token: '/v1/auth/token',
    refresh: '/v1/auth/refresh',
    revoke: '/v1/auth/revoke',

    // Self Service API — Claim Mapping
    analyses: '/v1/claim-mapping',
    analyze: '/v1/claim-mapping/analyze',
    analysis: (id: string) => `/v1/claim-mapping/${id}`,
    analysisReport: (id: string) => `/v1/claim-mapping/${id}/report`,

    // Self Service API — User Activation
    activate: '/v1/users/activate',

    // Self Service API — Credits
    credits: '/v1/credits',
    creditPackages: '/v1/credits/packages',
    creditCheckout: '/v1/credits/checkout',

    // Self Service API — Enterprise product admin surface (RFC-0211 A2).
    // org_admin-only; org is always derived from the JWT server-side, so
    // there is deliberately no org_id parameter here.
    enterprisePool: '/v1/enterprise/pool',
    enterpriseTeam: '/v1/enterprise/team',
    enterpriseTeamOrgAdmin: (userId: string) => `/v1/enterprise/team/${userId}/org-admin`,
    enterpriseMagicLink: '/v1/enterprise/magic-link',

    // ── Enterprise API (ent-api) — guest access (RFC-0510 W8) ────────────
    // ⚠️ A DIFFERENT SERVICE from the `/v1/enterprise/*` routes above. Those
    // are ss-api's; these are ent-api's, reached through `enterpriseApiClient`
    // and its own base URL. Paths are relative to that base, so passing one of
    // these to `apiClient` would silently hit ss-api and 404.
    //
    // `org_id` IS in the path — unlike ss-api's enterprise routes, which derive
    // the org from the JWT and therefore take no parameter. It is here because
    // a platform admin must be able to name an organisation, and the server
    // never trusts it: for an org_admin it is compared against their own
    // `org_id` claim and any mismatch is 403 `org_scope_denied`. An org admin
    // driving this UI passes their own organisation and nothing else, which is
    // why there is no organisation picker on the grant form.
    entGuests: (orgId: string) => `/v1/organizations/${orgId}/guests`,
    entGuest: (orgId: string, email: string) =>
      `/v1/organizations/${orgId}/guests/${encodeURIComponent(email)}`,

    // Self Service API — Feedback (per RFC-0124)
    feedback: '/v1/feedback',
    feedbackForAnalysis: (analysisId: string) => `/v1/feedback/${analysisId}`,

    // Self Service API — Events (forwarded to Tracking API)
    trackEvent: '/v1/events',

    // Upload
    upload: '/audit/upload',

    // Reports
    report: (id: string) => `/audit/reports/${id}`,
    reportClaims: (id: string) => `/audit/reports/${id}/claims`,

    // Share
    shareCreate: '/share',
    shareAccess: (token: string) => `/share/${token}`,

    // Workflows (RFC-0200 multi-workflow)
    workflows: '/v1/workflows',
    cases: '/v1/cases',

    // Advanced API
    advancedCases: '/v1/advanced/cases',
    // Batched dashboard listing — one call, paginated + server-side search.
    // Supersedes advancedCases + per-case /status fan-out for the dashboard.
    advancedWorkItemsList: '/v1/advanced/workitems',
    advancedWorkItems: (caseId: number) => `/v1/advanced/cases/${caseId}/workitems`,
    advancedContext: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/context`,
    advancedUploadUrl: '/v1/advanced/upload-url',
    advancedAttach: (caseId: number, wiId: number, artifact: string) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/artifacts/${artifact}/attach`,
    // Attach an EXISTING case document to a slot (RFC-0206 W13). NOT the same
    // as advancedAttach above — that one attaches a freshly-uploaded file.
    advancedAttachDocument: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/attach_document_async`,
    // Case-scoped document listing (RFC-0407 Group A). SS always applies
    // include_pending=true server-side; the picker sends it explicitly too so
    // the contract is visible at the call site.
    caseDocuments: (caseId: number) => `/v1/cases/${caseId}/documents`,
    advancedRun: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/run`,
    advancedCancel: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/cancel`,
    advancedRevert: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/revert`,
    advancedRemoveAttachment: (caseId: number, wiId: number, artifact: string) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/artifacts/${artifact}/remove`,
    // Editable documents: read the canonical STANDARD text + persist an edit.
    advancedCanonicalAttribute: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/tables/canonical-attribute`,
    advancedTableAttribute: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/tables/table-attribute`,
    advancedJobStatus: (jobId: string) => `/v1/advanced/jobs/${jobId}`,
    // SSE relay of Core's job-progress snapshots (RFC-0205). Byte-for-byte
    // passthrough with durable-log `id:` offsets; the client echoes the last
    // offset back as `Last-Event-ID` on reconnect. jobId may contain a slash
    // ("<userId>/<uuid>"), which the backend's `:path` converter preserves.
    advancedJobStream: (jobId: string) => `/v1/advanced/jobs/${jobId}/stream`,
    // SSE relay of Core's work-item roster snapshots (RFC-0205). Byte-for-byte
    // passthrough with durable-log `id:` offsets; the client echoes the last
    // offset back as `Last-Event-ID` on reconnect. Unlike the job stream this
    // stays open for the work item's whole life (it has many jobs), so a frame
    // is only ever a "roster moved" trigger — never a per-job terminal.
    advancedWorkItemStream: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/stream`,
    // SSE relay of Core's case-event snapshots (RFC-0205). Byte-for-byte
    // passthrough with durable-log `id:` offsets; the client echoes the last
    // offset back as `Last-Event-ID` on reconnect. Carries the CASE-scoped jobs
    // the work-item relay cannot — document uploads and preprocessing emit no
    // work_item_id — so a frame here is a "case moved" (e.g. an upload changed)
    // trigger. Stays open for the case's whole life; never a per-job terminal.
    advancedCaseStream: (caseId: number) =>
      `/v1/advanced/cases/${caseId}/stream`,
    // SSE relay of Core's ACCOUNT channel (RFC-0205 C9, `userevents:{uid}`;
    // ss-api builds the channel from the session, so there is no id to pass).
    // The only scope that can report ARRIVAL: a work item created, a case
    // shared with this user, a run starting on any case they read.
    // RFC 0180 — the multiplexed relay. One connection, a declared set of
    // aliases in the query (`?s=u&s=c12&s=w456`). Replaces the five per-scope
    // relays; they stay until the retirement gate names an environment.
    advancedMultiplexStream: '/v1/advanced/stream',
    advancedUserStream: '/v1/advanced/user/stream',
    // Best-effort cancel of an in-flight upload/processing job (jobId may
    // contain a slash, e.g. "<userId>/<uuid>").
    advancedJobCancel: (jobId: string) => `/v1/advanced/jobs/${jobId}/cancel`,
    // Figures (RFC-0162): async render-to-SVG + native-shape PPTX export.
    advancedFiguresSvg: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/figures/svg`,
    // Figure solution mutations (RFC-0177). One helper for the whole family:
    // the suffix IS Core's own route name, so a new engine action needs no
    // entry here and cannot be misspelled in two places.
    advancedFigureAction: (caseId: number, wiId: number, action: string) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/figures/${action}`,
    advancedPptx: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/documents/pptx`,
    advancedJobHistory: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/jobs`,
    // In-flight document uploads (upload rehydration after a page refresh).
    advancedActiveUploads: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/active-uploads`,
    advancedOutput: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/output`,
    /** RFC-0174 P2 — the work item's version-independent state (editing mode).
     *  No version segment: the state is not per version. */
    advancedWorkItemState: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/state`,
    advancedFindingRow: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/findings/row`,
    advancedDocxExport: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/documents/docx`,
    advancedDocxTracked: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/documents/docx/tracked`,
    advancedWorkItemVersions: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/versions`,
    // "Add Document": clone the current minor into a fresh major version,
    // flipping the work item back to upload mode (gated server-side on the
    // workflow's allowsVersionIncrement capability).
    advancedNextMajor: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/next_major`,
    // RFC-0163: redline current version against an arbitrary baseline
    // version (deletion-complete + move-aware). Distinct from the inline
    // 'previous' slot shipped in POST /output.
    advancedRedlineAgainst: (caseId: number, wiId: number) =>
      `/v1/advanced/cases/${caseId}/workitems/${wiId}/redline_against`,

    // ── Reviewer comments (RFC-0168 §8.6, proxied by ss-api at W24) ───────
    // NOT under `/v1/advanced/*`: these are ss-api's own synchronous comment
    // routes, in the `/v1/cases` family the participant surface uses. All four
    // are participant-or-owner, and the engine — never this client — decides
    // who may write what.
    workItemComments: (caseId: number, wiId: number) =>
      `/v1/cases/${caseId}/workitems/${wiId}/comments`,
    // `commentId` is a STRING id (`RC123`, or `RC123__R2` for a reply on the
    // PATCH route) and is echoed back exactly as the list sent it — see
    // `commentIdOf` in `components/work-item/reviewComments.ts`.
    workItemComment: (caseId: number, wiId: number, commentId: string) =>
      `/v1/cases/${caseId}/workitems/${wiId}/comments/${encodeURIComponent(commentId)}`,
    workItemCommentReplies: (caseId: number, wiId: number, commentId: string) =>
      `/v1/cases/${caseId}/workitems/${wiId}/comments/${encodeURIComponent(commentId)}/replies`,
  },

  // Upload constraints
  upload: {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['text/csv', 'application/vnd.ms-excel'],
    allowedExtensions: ['.csv'],
  },

  // Report retention
  retention: {
    days: 7,
    warningDays: 2, // Show warning when 2 or fewer days remaining
    urgentDays: 1,  // Show urgent when 1 or fewer days remaining
  },
};
